import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'usehooks-ts';
import {
  cn,
  Flex,
  Tabs,
  TabsList,
  TabsTrigger,
  toast,
} from '@kognitos/lattice';

import { formatDisplayName } from '@/shared/utils';

import {
  extractMetadataItems,
  isBrowserInstance,
  isDownloadable,
} from '../utils';
import {
  EOutputSourceView,
  type IOutputPreviewItem,
  type IPreviewLayoverProps,
} from '../types';
import { useIDPDocumentName, useOutputSourceViewText } from '../hooks';
import {
  getOutputCardTypeLabel,
  serializeOutputForClipboardHTML,
  serializeOutputForCopy,
} from './output-card-utils';
import { getOutputModalPresentation } from './output-presentation';

import { Overlay } from './PreviewLayover/Overlay';
import {
  OUTPUT_MODAL_DEFAULT_ZOOM_INDEX,
  OUTPUT_MODAL_ZOOM_LEVELS,
  OutputModalZoomControl,
  OutputModalZoomProvider,
} from './PreviewLayover/OutputModalZoom';
import { BrowserLayout } from './PreviewLayover/BrowserLayout';
import { DefaultLayout } from './PreviewLayover/DefaultLayout';
import { OutputModalCarousel } from './PreviewLayover/OutputModalCarousel';
import { OutputModalHeader } from './PreviewLayover/OutputModalHeader';
import './PreviewLayover/preview-layover.css';

const MODAL_TITLE_ID = 'output-preview-modal-title';

export function PreviewLayover({
  isOpen,
  outputName,
  outputValue,
  onClose,
  onDownload,
  isLoading = false,
  loadingLabel,
  className,
  navigationItems,
  showCloseButton = true,
}: IPreviewLayoverProps) {
  const { t } = useTranslation('outputs');
  const [, copyToClipboard] = useCopyToClipboard();
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [zoomIndex, setZoomIndex] = useState(OUTPUT_MODAL_DEFAULT_ZOOM_INDEX);
  const arrowFocusDirectionRef = useRef<1 | -1 | null>(null);
  const [sourceView, setSourceView] = useState<EOutputSourceView>(
    EOutputSourceView.PREVIEW,
  );

  const items = useMemo<IOutputPreviewItem[]>(() => {
    if (navigationItems?.length) return navigationItems;
    return [{ name: outputName, value: outputValue }];
  }, [navigationItems, outputName, outputValue]);

  // eslint-disable-next-line no-restricted-syntax -- reason: Reset modal-local navigation state when a newly opened collection becomes active.
  useEffect(() => {
    if (!isOpen) return;
    const nextIndex = items.findIndex(item => item.name === outputName);
    setActiveIndex(nextIndex >= 0 ? nextIndex : 0);
    setSelectedIndex(0);
    setZoomIndex(OUTPUT_MODAL_DEFAULT_ZOOM_INDEX);
    setSourceView(EOutputSourceView.PREVIEW);
  }, [isOpen, items, outputName]);

  // eslint-disable-next-line no-restricted-syntax -- reason: Clamp modal-local navigation after the caller removes collection items.
  useEffect(() => {
    setActiveIndex(index => Math.min(index, Math.max(items.length - 1, 0)));
  }, [items.length]);

  const activeItem = items[activeIndex] ?? {
    name: outputName,
    value: outputValue,
  };
  const metadataItems = extractMetadataItems(activeItem.value, activeItem.name);
  const selectedItem = metadataItems[selectedIndex] ?? metadataItems[0] ?? null;
  const displayedValue = selectedItem?.value ?? activeItem.value;
  const modalPresentation = getOutputModalPresentation(activeItem.value);
  // A sidebar needs the pane and the large shell whatever the value's variant, like a plain file list.
  const hasSidebar = !isLoading && metadataItems.length > 1;
  const shellSize = hasSidebar ? 'large' : modalPresentation.size;
  const shellLayout = hasSidebar ? 'wide' : modalPresentation.layout;
  const isBrowser = isBrowserInstance(activeItem.value);
  const sourceViewText = useOutputSourceViewText(activeItem.value);
  const hasSourceView = sourceViewText !== null;
  // While loading there is no value and often no name yet, so the filename the
  // caller already knows stands in rather than a generic "Preview".
  const idpDocument = useIDPDocumentName(activeItem.value);
  const title =
    idpDocument?.displayName ??
    activeItem.displayName ??
    (formatDisplayName(activeItem.name) ||
      loadingLabel ||
      t('outputs.modal.preview'));
  const typeLabel = getOutputCardTypeLabel(
    activeItem.value,
    modalPresentation.variant,
  );
  const handleDownload = idpDocument
    ? idpDocument.download
    : onDownload && isDownloadable(activeItem.value)
      ? () => onDownload(activeItem.value, activeItem.name)
      : undefined;

  const handleCopy = useCallback(async () => {
    if (!modalPresentation.copyable) return;
    const serialized = serializeOutputForCopy(
      activeItem.value,
      modalPresentation.variant,
    );
    const richHTML = serializeOutputForClipboardHTML(
      activeItem.value,
      modalPresentation.variant,
    );
    let copied = false;
    if (
      richHTML &&
      typeof ClipboardItem !== 'undefined' &&
      navigator.clipboard?.write
    ) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': new Blob([serialized], { type: 'text/plain' }),
            'text/html': new Blob([richHTML], { type: 'text/html' }),
          }),
        ]);
        copied = true;
      } catch {
        copied = await copyToClipboard(serialized);
      }
    } else {
      copied = await copyToClipboard(serialized);
    }
    if (copied) toast.success(t('outputs.card.copied'));
  }, [activeItem.value, copyToClipboard, modalPresentation, t]);

  const goTo = useCallback((index: number) => {
    setActiveIndex(index);
    setSelectedIndex(0);
    setZoomIndex(OUTPUT_MODAL_DEFAULT_ZOOM_INDEX);
    setSourceView(EOutputSourceView.PREVIEW);
  }, []);

  useHotkeys(
    'right,down,left,up',
    event => {
      // Preserve cursor movement and the embedded grid's own keyboard navigation.
      if (
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        (event.target instanceof Element &&
          event.target.closest(
            'input, textarea, select, [contenteditable="true"], [role="grid"], [role="listbox"], [role="slider"]',
          ))
      )
        return;
      event.preventDefault();
      const direction =
        event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = Math.max(
        0,
        Math.min(items.length - 1, activeIndex + direction),
      );
      if (nextIndex !== activeIndex) {
        arrowFocusDirectionRef.current = direction;
        goTo(nextIndex);
      }
    },
    { enabled: isOpen && items.length > 1 },
    [activeIndex, items.length, goTo],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: arrow-key navigation must hand focus to the carousel button it paged toward, and that button's disabled state is only correct once this render lands.
  useEffect(() => {
    const direction = arrowFocusDirectionRef.current;
    if (direction === null) return;
    arrowFocusDirectionRef.current = null;
    const preferredSide = direction === 1 ? 'next' : 'previous';
    const fallbackSide = direction === 1 ? 'previous' : 'next';
    const preferredButton = document.querySelector<HTMLButtonElement>(
      `[data-output-modal-carousel-button="${preferredSide}"]`,
    );
    // Fall back to the opposite carousel button when paging disables the one just pressed.
    const target =
      preferredButton && !preferredButton.disabled
        ? preferredButton
        : document.querySelector<HTMLButtonElement>(
            `[data-output-modal-carousel-button="${fallbackSide}"]`,
          );
    target?.focus();
  }, [activeIndex]);

  useHotkeys('esc', onClose, {
    enabled: isOpen,
    preventDefault: true,
  });

  if (!isOpen) return null;

  const header = (
    <OutputModalHeader
      title={title || t('outputs.modal.preview')}
      typeLabel={typeLabel}
      onCopy={modalPresentation.copyable ? handleCopy : undefined}
      onDownload={handleDownload}
      onClose={onClose}
      showCloseButton={showCloseButton}
      titleId={MODAL_TITLE_ID}
      viewControl={
        modalPresentation.zoomable ? (
          <OutputModalZoomControl
            zoomIndex={zoomIndex}
            onZoomIndexChange={setZoomIndex}
          />
        ) : hasSourceView ? (
          // Tabs without a `TabsContent`: both readings mount into the modal's
          // single content area below, so there is no panel to switch here.
          <Tabs
            value={sourceView}
            onValueChange={value => setSourceView(value as EOutputSourceView)}
          >
            <TabsList className="mr-1 text-foreground">
              <TabsTrigger value={EOutputSourceView.PREVIEW}>
                {t('outputs.modal.sourceViewPreview')}
              </TabsTrigger>
              <TabsTrigger value={EOutputSourceView.SOURCE}>
                {t('outputs.modal.sourceViewSource')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        ) : undefined
      }
    />
  );

  // Loading goes through `DefaultLayout` too, so only the content area swaps, avoiding a reflow when bytes arrive.
  const body = (
    <DefaultLayout
      value={displayedValue}
      outputName={activeItem.name}
      metadataItems={metadataItems}
      selectedIndex={selectedIndex}
      onSelect={setSelectedIndex}
      onClose={onClose}
      onDownload={onDownload}
      layout={shellLayout}
      isLoading={isLoading}
      sourceView={hasSourceView ? sourceView : undefined}
      sourceText={hasSourceView ? sourceViewText : null}
    />
  );

  return createPortal(
    <Overlay
      onClose={onClose}
      ariaLabelledBy={MODAL_TITLE_ID}
      size={isBrowser ? 'lg' : 'md'}
      // The browser takes the lg box as-is; the shell size classes are unlayered
      // CSS and would cap it back down.
      className={
        isBrowser
          ? className
          : cn(
              `output-modal-shell--${shellSize}`,
              `output-modal-shell--${shellLayout}`,
              className,
            )
      }
    >
      {isBrowser ? (
        // A browser session brings its own frame, header and close button, so
        // it bypasses the shell's header and content surface entirely.
        <BrowserLayout
          value={displayedValue}
          outputName={activeItem.name}
          onClose={onClose}
        />
      ) : (
        <>
          {header}
          <OutputModalZoomProvider zoom={OUTPUT_MODAL_ZOOM_LEVELS[zoomIndex]}>
            {/* Pane types get the bordered, tinted content surface; plain
                types render with no outer container so their own sheet is
                the body. */}
            {modalPresentation.contentMode === 'pane' || hasSidebar ? (
              <Flex
                position="relative"
                flex="1"
                align="stretch"
                overflow="hidden"
                className="output-modal-content-surface shadow-sm rounded-xl min-h-0"
              >
                {body}
              </Flex>
            ) : (
              body
            )}{' '}
          </OutputModalZoomProvider>
        </>
      )}
      {items.length > 1 && (
        <OutputModalCarousel
          currentIndex={activeIndex}
          itemCount={items.length}
          onPrevious={activeIndex > 0 ? () => goTo(activeIndex - 1) : undefined}
          onNext={
            activeIndex < items.length - 1
              ? () => goTo(activeIndex + 1)
              : undefined
          }
        />
      )}
    </Overlay>,
    document.body,
  );
}
